import { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useUser, useAccessToken } from '@features/user';
import { CircularProgress, Box, Typography } from '@mui/material';
import APIService from '../services/APIService';
import { useEventTracking } from '@services/statsig';

const GoogleAuthCallback = () => {
  const navigate = useNavigate();
  const { userState } = useUser();
  const { getAccessToken } = useAccessToken();
  const { googleCalendar } = useEventTracking();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const processedRef = useRef(false);

  useEffect(() => {
    // Only proceed if we have the user state and haven't processed the callback yet
    if (!userState?.user_id || processedRef.current) {
      return;
    }

    const handleCallback = async () => {
      // Mark as processed immediately to prevent duplicate processing
      processedRef.current = true;

      try {
        const accessToken = await getAccessToken();

        console.log(searchParams.toString());

        // Get the authorization code and state from the URL
        const code = searchParams.get('code');
        const state = searchParams.get('state');
        const error = searchParams.get('error');

        // If Google returned an error, handle it
        if (error) {
          console.error('Google authorization error:', error);
          googleCalendar.connectionFailed({ 
            error_type: 'oauth_error',
            error_message: error 
          });
          setStatus('error');
          setTimeout(() => navigate('/user'), 2000);
          return;
        }

        // Verify that we got a code and state
        if (!code || !state) {
          googleCalendar.connectionFailed({ 
            error_type: 'missing_params',
            error_message: 'Missing authorization code or state' 
          });
          setStatus('error');
          setTimeout(() => navigate('/user'), 2000);
          return;
        }

        // Verify the state parameter matches what we stored (prevents CSRF)
        const storedState = localStorage.getItem('google_oauth_state');
        if (state !== storedState) {
          console.error("State parameter doesn't match stored state");
          googleCalendar.connectionFailed({ 
            error_type: 'state_mismatch',
            error_message: 'CSRF protection state mismatch' 
          });
          setStatus('error');
          setTimeout(() => navigate('/user'), 2000);
          return;
        }

        // Clear the stored state
        localStorage.removeItem('google_oauth_state');

        // Send the authorization code and redirect uri to our backend
        const redirectUri = `${window.location.origin}/google-auth-callback`;
        const response = await APIService.makeAPIPostRequest({
          requestString: `/v2/provider/integrations/google`,
          accessToken: accessToken,
          body: {
            code: code,
            provider_id: userState.user_id as string,
            redirect_uri: redirectUri,
          },
        });

        if (!response.ok) {
          throw new Error(response.error?.message || 'Failed to exchange authorization code');
        }

        // Success! (204 response)
        googleCalendar.connected({});
        setStatus('success');
        setTimeout(() => navigate('/user'), 2000);
      } catch (error) {
        console.error('Error handling Google OAuth callback:', error);
        googleCalendar.connectionFailed({ 
          error_type: 'api_error',
          error_message: error instanceof Error ? error.message : 'Unknown error'
        });
        setStatus('error');
        setTimeout(() => navigate('/user'), 2000);
      }
    };

    handleCallback();
  }, [searchParams, navigate, getAccessToken, userState]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '60vh',
        gap: 2,
      }}
    >
      {status === 'loading' && (
        <>
          <CircularProgress size={60} />
          <Typography variant="h5">Connecting Google Calendar...</Typography>
        </>
      )}
      {status === 'success' && (
        <>
          <Typography variant="h5" color="success.main">
            Google Calendar successfully connected!
          </Typography>
          <Typography>Redirecting to your profile...</Typography>
        </>
      )}
      {status === 'error' && (
        <>
          <Typography variant="h5" color="error.main">
            Failed to connect Google Calendar
          </Typography>
          <Typography>Redirecting to your profile...</Typography>
        </>
      )}
    </Box>
  );
};

export default GoogleAuthCallback;
